import dayjs from "dayjs";

export const QUALIFIED_STATUS_DEFAULT = ["Contratado", "Entregue", "Finalizado"];
export const boletoStatus = [
  "Pendente",
  "Emitido",
  "Pago",
  "Vencido",
  "Cancelado",
];
export const FINANCIAL_STATUS_DEFAULT = [
  "pendente",
  "emitido",
  "pago",
  "vencido",
  "cancelado",
];
export const BOLETO_ACTION_VISIBLE_STATUSES = new Set(["emitido", "vencido"]);
export const FINANCIAL_STATUS_OPTIONS = boletoStatus.map((label) => ({
  value: label.toLowerCase(),
  label,
}));
export const MAX_FILTER_FETCH_ALL_PAGES = 80;

const FINANCIAL_STATUS_SET = new Set(FINANCIAL_STATUS_DEFAULT);
const INVALID_FINANCIAL_STATUS_FALLBACK = "cancelado";

function normalizeStatusKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

const FINANCIAL_STATUS_ALIASES = {
  pendente: "pendente",
  pending: "pendente",
  aberto: "pendente",
  "em aberto": "pendente",
  "a emitir": "pendente",
  "pendente boleto": "pendente",
  "pendente liberacao": "pendente",
  "pendente de liberacao": "pendente",

  emitido: "emitido",
  emitida: "emitido",
  issued: "emitido",
  enviado: "emitido",
  enviada: "emitido",
  "boleto emitido": "emitido",
  "boleto enviado": "emitido",
  "enviado boleto": "emitido",
  "pagamento solicitado": "emitido",
  "solicitado pagamento": "emitido",
  "solicitar pagamento": "emitido",

  pago: "pago",
  paga: "pago",
  paid: "pago",
  baixado: "pago",
  baixada: "pago",
  liquidado: "pago",
  liquidada: "pago",
  quitado: "pago",
  quitada: "pago",

  vencido: "vencido",
  vencida: "vencido",
  overdue: "vencido",
  atrasado: "vencido",
  atrasada: "vencido",
  "em atraso": "vencido",

  cancelado: "cancelado",
  cancelada: "cancelado",
  canceled: "cancelado",
  cancelled: "cancelado",
  bloqueado: "cancelado",
  bloqueada: "cancelado",
};

export function normalizeFinancialStatusValue(value) {
  const key = normalizeStatusKey(value);
  const normalized = FINANCIAL_STATUS_ALIASES[key] || key;
  return FINANCIAL_STATUS_SET.has(normalized) ? normalized : "";
}

export function normalizeStoredFinancialStatusValue(value, fallback = "") {
  const normalized = normalizeFinancialStatusValue(value);
  if (normalized) return normalized;

  const hasLegacyValue = String(value ?? "").trim().length > 0;
  if (hasLegacyValue) return INVALID_FINANCIAL_STATUS_FALLBACK;

  return normalizeFinancialStatusValue(fallback);
}

export function normalizeFinancialStatusValues(values, fallback = []) {
  const source = Array.isArray(values)
    ? values
    : values != null && String(values).trim()
      ? [values]
      : [];
  const normalized = Array.from(
    new Set(source.map(normalizeFinancialStatusValue).filter(Boolean)),
  );
  return normalized.length ? normalized : [...fallback];
}

export function formatFinancialStatusValue(value) {
  const normalized = normalizeFinancialStatusValue(value);
  if (!normalized) return "";
  return (
    FINANCIAL_STATUS_OPTIONS.find((option) => option.value === normalized)
      ?.label || normalized
  );
}

export function moneyToInt(value) {
  if (value == null) return 0;
  if (typeof value === "number") return Math.round(value);
  const str = String(value);
  return Math.round(Number(str.replace(/\./g, "").replace(",", ".")));
}

export function appendMoneyFilterToV2Query(q, fieldValue, queryKey) {
  if (fieldValue == null || fieldValue === "") return;
  if (typeof fieldValue === "object" && fieldValue.operator) {
    if (fieldValue.operator === "equals" && fieldValue.value !== undefined) {
      q[queryKey] = String(Number(fieldValue.value));
    }
  } else {
    const n =
      typeof fieldValue === "number" ? fieldValue : moneyToInt(fieldValue);
    q[queryKey] = String(Number(n));
  }
}

export function serializeFilterKey(dateRange, columnFilters) {
  try {
    return JSON.stringify({
      dr: Array.isArray(dateRange)
        ? dateRange.map((d) =>
            d == null ? null : dayjs(d).format("YYYY-MM-DD"),
          )
        : [],
      cf: columnFilters,
    });
  } catch {
    return "";
  }
}

export function fingerprintFreightParams(params) {
  return [
    params.uidShipper ?? "",
    params.affiliates ?? "",
    String(params.viewAll ?? ""),
    params.filterKey ?? "",
  ].join("|");
}

function arraysEqualSorted(a, b) {
  const sa = [...(a || [])].map(String).sort();
  const sb = [...(b || [])].map(String).sort();
  if (sa.length !== sb.length) return false;
  return sa.every((value, index) => value === sb[index]);
}

export function buildControlPanelV2QueryParams(dateRange, columnFilters) {
  const q = { firstDeliveryCompleted: "true" };

  if (dateRange?.length) {
    const [start, end] = dateRange;
    if (start) q.dateColetaDe = dayjs(start).format("YYYY-MM-DD");
    if (end) q.dateColetaAte = dayjs(end).format("YYYY-MM-DD");
  }

  if (columnFilters?.numberSerial) {
    q.numberSerial = String(columnFilters.numberSerial).trim();
  }
  if (columnFilters?.shipperName) {
    q.shipperName = String(columnFilters.shipperName).trim();
  }

  appendMoneyFilterToV2Query(q, columnFilters?.freightValue, "freightValue");

  const statusFinancialFilter = columnFilters?.statusFinancial;
  if (
    Array.isArray(statusFinancialFilter) ||
    (typeof statusFinancialFilter === "string" && statusFinancialFilter.trim())
  ) {
    const normalized = normalizeFinancialStatusValues(statusFinancialFilter);
    const isDefaultSelection = arraysEqualSorted(
      normalized,
      FINANCIAL_STATUS_DEFAULT,
    );
    if (!isDefaultSelection) {
      if (normalized.length === 1) {
        q.statusFinancial = normalized[0];
      } else if (normalized.length > 1) {
        q.statusFinancial = normalized;
      }
    }
  }

  return q;
}

export function isFinancialStatusFilterNonDefault(columnFilters) {
  const statusFinancialFilter = columnFilters?.statusFinancial;
  if (
    Array.isArray(statusFinancialFilter) ||
    (typeof statusFinancialFilter === "string" && statusFinancialFilter.trim())
  ) {
    const normalized = normalizeFinancialStatusValues(statusFinancialFilter);
    return (
      normalized.length > 0 &&
      !arraysEqualSorted(normalized, FINANCIAL_STATUS_DEFAULT)
    );
  }
  return false;
}

export function matchesDateInRange(value, range) {
  if (!range?.length || !range[0] || !range[1]) return true;
  if (value == null || value === "") return false;

  const date = dayjs(value);
  if (!date.isValid()) return false;

  const start = dayjs(range[0]).startOf("day");
  const end = dayjs(range[1]).endOf("day");
  return !date.isBefore(start) && !date.isAfter(end);
}

export function applyClientDateFilters(rows, columnDateRanges = {}) {
  let result = rows || [];

  const issueRange = columnDateRanges?.issueDate;
  if (issueRange?.length === 2 && issueRange[0] && issueRange[1]) {
    result = result.filter((row) =>
      matchesDateInRange(row?.issueDate, issueRange),
    );
  }

  const dueRange = columnDateRanges?.dueDate;
  if (dueRange?.length === 2 && dueRange[0] && dueRange[1]) {
    result = result.filter((row) => {
      if (row?.installmentNumber != null) {
        return matchesDateInRange(row?.dueDate, dueRange);
      }

      const installments = row?.dueDateInstallments || [];
      const dates =
        installments.length > 1 ? installments : [row?.dueDate].filter(Boolean);
      if (!dates.length) return false;
      return dates.some((date) => matchesDateInRange(date, dueRange));
    });
  }

  return result;
}

export function hasClientDateFilters(columnDateRanges) {
  const issue = columnDateRanges?.issueDate;
  const due = columnDateRanges?.dueDate;
  return (
    (Array.isArray(issue) && issue.length === 2 && issue[0] && issue[1]) ||
    (Array.isArray(due) && due.length === 2 && due[0] && due[1])
  );
}

export function parseStoredDateRange(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== 2) return [];
    const start = dayjs(parsed[0]);
    const end = dayjs(parsed[1]);
    if (!start.isValid() || !end.isValid()) return [];
    return [start, end];
  } catch {
    return [];
  }
}

export function serializeDateRangeForStorage(range) {
  if (!range?.length || !range[0] || !range[1]) return null;
  return JSON.stringify([
    dayjs(range[0]).format("YYYY-MM-DD"),
    dayjs(range[1]).format("YYYY-MM-DD"),
  ]);
}

export function hasActiveControlPanelFilters(
  dateRange,
  columnFilters,
  columnDateRanges,
) {
  if (dateRange?.length) return true;
  if (hasClientDateFilters(columnDateRanges)) return true;
  if (columnFilters?.numberSerial) {
    const value = String(columnFilters.numberSerial).trim();
    if (value) return true;
  }
  if (columnFilters?.shipperName) {
    const value = String(columnFilters.shipperName).trim();
    if (value) return true;
  }
  if (
    columnFilters?.freightValue != null &&
    columnFilters?.freightValue !== ""
  ) {
    return true;
  }

  return isFinancialStatusFilterNonDefault(columnFilters);
}

export function mergeFreightRowsById(rows) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (!row?.id) return;
    const prev = byId.get(row.id);
    byId.set(row.id, prev ? { ...prev, ...row } : row);
  });
  return Array.from(byId.values());
}

export function hasBoletoAttachmentOnRecord(record) {
  const attachment = record?.boletoAttachment;
  if (attachment == null || attachment === "") return false;

  if (typeof attachment === "string") {
    return attachment.trim().length > 0;
  }

  if (typeof attachment === "object") {
    return Boolean(
      String(attachment.url || attachment.downloadUrl || "").trim() ||
        String(
          attachment.name || attachment.originalName || attachment.fileName || "",
        ).trim(),
    );
  }

  return false;
}

export function canShowFreightControlBoletoAction(record) {
  const status = normalizeFinancialStatusValue(
    record?.statusFinancial ?? record?.financialStatus,
  );

  if (!BOLETO_ACTION_VISIBLE_STATUSES.has(status)) {
    return false;
  }

  return hasBoletoAttachmentOnRecord(record);
}

export function freightControlStorageKey(uid, key) {
  return `${uid}:freightControl:${key}`;
}

export function resetCursorMapFromPage(cursorMapRef, startPage) {
  if (!Number.isFinite(startPage)) return;
  const nextMap = new Map();
  cursorMapRef.current.forEach((value, key) => {
    if (key < startPage) nextMap.set(key, value);
  });
  cursorMapRef.current = nextMap;
}
